import { useEffect, useMemo, useState } from 'react';
import { Box, Container, Grid, makeStyles, Snackbar, Theme, Typography } from '@material-ui/core';
import { find, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { PrimaryButton } from 'src/components/ui/Buttons';
import CommandUI, { CommandUIPropsData } from 'src/components/ui/commandUI';
import LoadWithSpinner from 'src/components/ui/Loading/LoadWithSpinner';
import Notification from 'src/components/ui/notification';
import Selector from './selector';
import CommandTable from 'src/components/ui/commandUI/table';

import { darkGreen, primaryColor } from 'src/data/colors';

import { getCommands, runCommand } from 'src/state/entities/commands/actions';
import { getCommandsList } from 'src/state/entities/commands/selectors';
import { getSiteContext, getSiteLastQueryTime } from 'src/state/contexts/site';

import { Parameter, CommandRequest } from './types';
import Info from '@material-ui/icons/Info';
import StatusIndicator from 'src/components/ui/statusIndicator';

/* Set to true to show a few commands (the ones included in COMMANDS_ALLOWED_DATA).
I used this to avoid removing all the logic to show all commands and to avoid recreating the UI later.
To show all commands, set to false or remove all the logic related to REDUCE_COMMANDS 
*/
const REDUCE_COMMANDS = true;
const COMMANDS_ALLOWED_DATA = [
  {
    device: 'guardian',
    commands: [
      {
        name: 'getProductList',
        description: 'See what is currently attached to the gateway.',
        friendly: "See what's connected",
        parameters: [],
      },
      {
        description: 'The gateway will immediately send all datapoints.',
        friendly: 'Check-in now',
        name: 'forceSendTelemetry',
        parameters: [],
      },
    ],
  },
];

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: 'unset',
    padding: '40px 0 0',
  },
  button: {
    background: primaryColor,
    marginTop: 30,
    width: 'fit-content',
    '&:hover': {
      background: darkGreen,
    },
  },
  formInputs: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 25px',
  },
  inputsContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '50%',
    padding: '10px',
  },
  responseContainer: {
    margin: '10px',
    maxWidth: 'unset',
  },
  error: {
    fontSize: 20,
    margin: '24px 48px 0',
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      margin: '16px 24px',
    },
  },
  description: {
    display: 'flex',

    '& svg': {
      marginRight: 10,
    },
  },
}));

const Commands = () => {
  const [commandVariables, setCommandVariables] = useState<Parameter[] | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<string>('');
  const [showResponse, setShowResponse] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [inputValues, setInputValues] = useState<
    Record<string, string | number | string[] | boolean>
  >({});
  const [inputWithError, setInputWithError] = useState<boolean>(false);
  const [deviceSelected, setDeviceSelected] = useState<string>(REDUCE_COMMANDS ? 'guardian' : '');
  const commandsData = useSelector(getCommandsList);
  const site = useSelector(getSiteContext);
  const { id: siteId } = site;
  const dispatch = useDispatch();
  const siteFetchTime = useSelector(getSiteLastQueryTime);

  const devicesList = useMemo(() => {
    return commandsData.commands.map(({ device }) => device);
  }, [commandsData]);

  const commandsFilteredByDevice = useMemo(() => {
    if (REDUCE_COMMANDS) {
      return COMMANDS_ALLOWED_DATA;
    }

    return commandsData.commands.filter(({ device }) => device === deviceSelected);
  }, [commandsData, deviceSelected]);

  const showMessage = useMemo(() => {
    return (
      showNotification &&
      ((commandsData.error && !REDUCE_COMMANDS) ||
        commandsData.errorResponse ||
        commandsData.success)
    );
  }, [commandsData.error, commandsData.errorResponse, commandsData.success, showNotification]);

  const classes = useStyles();

  const getCommandVariables = (commandName: string) => {
    const command = commandsFilteredByDevice[0].commands.find(
      (command) => command.name === commandName
    );

    return command ? command.parameters : [];
  };

  const findCommandName = (
    friendlyName: string,
    commands: { name: string; friendly?: string }[]
  ) => {
    const command = find(commands, (command) => command.friendly === friendlyName);
    return command?.name;
  };

  const sentCommand = () => {
    const request = {
      commandName: findCommandName(selectedCommand, commandsFilteredByDevice[0].commands),
      variables: Object.entries(inputValues).map(([name, value]) => ({ name, value })),
    } as CommandRequest;
    dispatch(runCommand.request(siteId, request));

    setShowResponse(true);
  };

  const handleChangeCommand = (value: string | null) => {
    setShowResponse(false);
    setSelectedCommand(value as string);
    setInputValues({});
    setCommandVariables(getCommandVariables(value as string));
  };

  const handleChangeInput = (id: string, value: string | number | string[] | boolean) => {
    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      [id]: value,
    }));
  };

  const isFormValid = useMemo(() => {
    return !Object.values(inputValues).some((value) => value === '') && !inputWithError;
  }, [inputValues, inputWithError]);

  useEffect(() => {
    if (selectedCommand) {
      const command = commandsFilteredByDevice[0].commands.find(
        (command) => command.name === selectedCommand
      );

      if (!command) {
        setInputValues({});
      } else {
        const newValues = command.parameters.reduce((values, parameter) => {
          values[parameter.name] = parameter.default || (parameter.type === 'bitmap' ? [] : '');
          return values;
        }, {});
        setInputValues(newValues);
      }
    }
  }, [selectedCommand]);

  useEffect(() => {
    if (siteId !== commandsData.siteId) {
      dispatch(getCommands.request(siteId));
    }
  }, [commandsData]);

  useEffect(() => {
    if (commandsData.error || commandsData.errorResponse || commandsData.success) {
      setShowNotification(true);
    }
  }, [commandsData.error, commandsData.errorResponse, commandsData.success]);

  const changeDevice = (newValue: string) => {
    setDeviceSelected(newValue);
    setSelectedCommand('');
    setInputValues({});
    setCommandVariables(null);
    setShowResponse(false);
  };

  if (commandsData.loading) {
    return (
      <Container className={classes.container}>
        <LoadWithSpinner />
      </Container>
    );
  }

  if (commandsData.error && !REDUCE_COMMANDS) {
    return (
      <Container className={classes.error}>
        <Typography variant="h6">Error loading commands</Typography>
      </Container>
    );
  }

  return (
    <Container className={classes.container}>
      <Grid container>
        <Grid item xs={12}>
          <Box display="flex" marginLeft={3} alignItems="center">
            Site online status:
            <StatusIndicator
              isOnline={site.isOnline}
              siteFetchTime={siteFetchTime ? new Date(siteFetchTime) : undefined}
            />
          </Box>
        </Grid>
      </Grid>
      <>
        {!REDUCE_COMMANDS && (
          <Selector
            id="device-select"
            label="Select device"
            onChange={(event, newValue: string | null) => changeDevice(newValue || '')}
            options={devicesList}
            value={deviceSelected}
          />
        )}
        <Selector
          disabled={!deviceSelected}
          id="command-select"
          label="Select command"
          value={selectedCommand}
          onChange={(event, newValue: string | null) => handleChangeCommand(newValue || '')}
          options={
            commandsFilteredByDevice[0]?.commands.map(({ name, friendly }) => friendly || name) ||
            []
          }
        >
          <PrimaryButton className={classes.button} onClick={sentCommand} disabled={!isFormValid}>
            Send command
          </PrimaryButton>
        </Selector>
        {selectedCommand && (
          <Container className={classes.description}>
            <Info />
            <Typography variant="body1">
              {
                commandsFilteredByDevice[0]?.commands.find(
                  (command) => command.friendly === selectedCommand
                )?.description
              }
            </Typography>
          </Container>
        )}
        {commandVariables?.length && commandVariables.length > 0 && !isEmpty(inputValues) ? (
          <form className={classes.formInputs}>
            <div className={classes.inputsContainer}>
              <Typography variant="h6">Request data</Typography>
              {commandVariables.map((variable, index) => (
                <CommandUI
                  key={index}
                  dataType={variable.type}
                  props={{
                    ...(variable as unknown as CommandUIPropsData),
                    label: variable.name,
                    onChange: handleChangeInput,
                    setInputsError: setInputWithError,
                    value: inputValues[variable.name],
                  }}
                />
              ))}
            </div>
          </form>
        ) : null}
        {commandsData.loadingResponse && <LoadWithSpinner />}
        {commandsData.response && showResponse && typeof commandsData.response === 'string' && (
          <Container className={classes.responseContainer}>
            <Typography variant="h6">Response data</Typography>
            <Typography variant="body1">{commandsData.response}</Typography>
          </Container>
        )}
        {typeof commandsData.response === 'object' && showResponse && (
          <Container className={classes.responseContainer}>
            <CommandTable {...commandsData.response} />
          </Container>
        )}
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={3000}
          open={showMessage}
          onClose={() => setShowNotification(false)}
        >
          {commandsData.success ? (
            <Notification severity="success">Command run successfully</Notification>
          ) : (
            <Notification severity="error">
              {`Error: ${
                commandsData.error && !REDUCE_COMMANDS ? 'loading commands' : 'sending command'
              }`}
            </Notification>
          )}
        </Snackbar>
      </>
    </Container>
  );
};

export default Commands;
